import React from 'react';
import { Trash2, CheckCircle, Clock, AlertTriangle, GripVertical } from 'lucide-react';
import Button from './Button';
import { ProductionTask } from '../../types';
import { formatCurrency } from '../../utils/formatters';
import { formatArgentinaDate } from '../../utils/dateUtils';

interface KanbanViewProps {
  tasks: ProductionTask[];
  onEditTask: (task: ProductionTask) => void;
  onDeleteTask: (id: string) => void;
  getSupplierName: (id: string | null) => string;
  onStatusChange: (taskId: string, newStatus: 'pending' | 'in_progress' | 'finished') => void;
}

const KanbanView: React.FC<KanbanViewProps> = ({
  tasks,
  onEditTask,
  onDeleteTask,
  getSupplierName,
  onStatusChange,
}) => {
  // Group tasks by status
  const pendingTasks = tasks.filter(task => task.status === 'pending');
  const inProgressTasks = tasks.filter(task => task.status === 'in_progress');
  const finishedTasks = tasks.filter(task => task.status === 'finished');

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, task: ProductionTask) => {
    e.dataTransfer.setData('taskId', task.id);
    // Add some visual feedback
    e.currentTarget.classList.add('opacity-50');
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    // Add some visual feedback
    e.currentTarget.classList.add('bg-gray-100', 'dark:bg-gray-800');
  };
  
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    // Remove visual feedback
    e.currentTarget.classList.remove('bg-gray-100', 'dark:bg-gray-800');
  };
  
  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    // Remove opacity from the dragged item
    e.currentTarget.classList.remove('opacity-50');
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, newStatus: 'pending' | 'in_progress' | 'finished') => {
    e.preventDefault();
    // Remove visual feedback
    e.currentTarget.classList.remove('bg-gray-100', 'dark:bg-gray-800');
    
    const taskId = e.dataTransfer.getData('taskId');
    if (taskId) {
      onStatusChange(taskId, newStatus);
    }
  };

  // Function to render a task card
  const renderTask = (task: ProductionTask) => {
    return (
      <div 
        key={task.id} 
        draggable
        onDragStart={(e) => handleDragStart(e, task)}
        onDragEnd={handleDragEnd}
        className={`p-4 rounded-lg shadow-sm mb-3 border-l-4 ${
          task.status === 'pending' 
            ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500 dark:border-amber-400' 
            : task.status === 'in_progress'
            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-400'
            : 'bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400'
        }`}
        onClick={() => onEditTask(task)}
        style={{ cursor: 'pointer' }}
      >
        <div className="flex justify-between items-start">
          <div className="flex items-start">
            <div className="mr-2 text-gray-400 cursor-move">
              <GripVertical className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-gray-900 dark:text-white">{task.task_name}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {getSupplierName(task.supplier_id)}
              </p>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                {task.start_date && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                    <Clock className="h-3 w-3 mr-1" />
                    {formatArgentinaDate(task.start_date)}
                  </span>
                )}
                {task.end_date && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {formatArgentinaDate(task.end_date)}
                  </span>
                )}
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200">
                  {formatCurrency(task.cost)}
                </span>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            icon={<Trash2 className="h-4 w-4" />}
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering the parent onClick
              onDeleteTask(task.id);
            }}
          >
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Pending Column */}
      <div 
        className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, 'pending')}
      >
        <div className="flex items-center mb-4">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mr-2" />
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
            Pendiente <span className="text-sm font-normal">({pendingTasks.length})</span>
          </h3>
        </div>
        <div className="space-y-3">
          {pendingTasks.map(renderTask)}
          {pendingTasks.length === 0 && (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
              No hay tareas pendientes
            </div>
          )}
        </div>
      </div>

      {/* In Progress Column */}
      <div 
        className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, 'in_progress')}
      >
        <div className="flex items-center mb-4">
          <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
            En Proceso <span className="text-sm font-normal">({inProgressTasks.length})</span>
          </h3>
        </div>
        <div className="space-y-3">
          {inProgressTasks.map(renderTask)}
          {inProgressTasks.length === 0 && (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
              No hay tareas en proceso
            </div>
          )}
        </div>
      </div>

      {/* Finished Column */}
      <div 
        className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, 'finished')}
      >
        <div className="flex items-center mb-4">
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
            Terminado <span className="text-sm font-normal">({finishedTasks.length})</span>
          </h3>
        </div>
        <div className="space-y-3">
          {finishedTasks.map(renderTask)}
          {finishedTasks.length === 0 && (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
              No hay tareas terminadas
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KanbanView;